import Color from "ui/helpers/Color";

/**
 * Flood Fill Helper for Canvas object
 */
class CanvasFloodFill {
	/**
	 * Class constructor
	 *
	 * @param {T_CanvasElement} fillCanvas
	 */
	constructor(fillCanvas) {
		/**
		 * @type {T_CanvasElement}
		 * @private
		 */
		this._fillCanvas = fillCanvas;

		/**
		 * @type {ImageData}
		 * @private
		 */
		this._fillPixels = null;

		/**
		 * @type {Uint8Array}
		 * @private
		 */
		this._filledPixels = null;

		/**
		 * @type {number}
		 * @private
		 */
		this._width = 0;

		/**
		 * @type {number}
		 * @private
		 */
		this._height = 0;

		/**
		 * @type {Color}
		 * @private
		 */
		this._transparentColor = new Color(0, 0, 0, 0);
	}

	/**
	 * Fills area from the specified point
	 *
	 * @param {number} x
	 * @param {number} y
	 * @param {Color} color
	 * @returns {boolean}
	 */
	fill(x, y, color) {
		if (color.rgba === 0) {
			// replace transparent black color because it uses in algorithm below
			color = new Color(255, 0, 255, 0);
		}
		x <<= 0;
		y <<= 0;
		let w = this._width = this._fillCanvas.el.width,
			h = this._height = this._fillCanvas.el.height;

		let fillData = this._fillCanvas.ctx.getImageData(0, 0, w, h);
		this._fillPixels = fillData.data;
		this._filledPixels = new Uint8Array(w * h);

		let fillColor = color,
			targetColor = this._getColor(x, y),
			openList = [{x, y, status: 1}], northOpened = false, southOpened = false;

		while (openList.length > 0) {
			let {x, y, status} = openList.shift();
			if (this._getColor(x, y, fillColor).rgba === fillColor.rgba) {
				// the point already has necessary color
				continue;
			}

			// fill the point
			if (!this._fillPoint(x, y, fillColor, status === 2 ? 0.6 : 1)) {
				// cannot fill point, end of range
				return false;
			}

			if (status !== 1) {
				// the point has partially match with the target color
				// skip make horizontal scanning
				continue;
			}

			// fills only points at east and west sides, prepare new open points at north and south
			for (let {initX, step} of [{initX: x + 1, step: 1}, {initX: x - 1, step: -1}]) {
				// scan east/west
				northOpened = false;
				southOpened = false;
				for (let sx = initX; sx >= 0 && sx < this._width; sx += step) {
					status = this._getColorStatus(sx, y, fillColor, targetColor);
					if (status > 0) {
						// the point match to the target color
						// fill it
						if (!this._fillPoint(sx, y, fillColor, status === 2 ? 0.6 : 1)) {
							// cannot fill point, end of range
							return false;
						}
						if (status === 2) {
							// stop scanning
							break;
						}
					} else {
						// the point already has necessary color or has another color
						// stop scanning
						break;
					}
					// check north
					if (y > 0) {
						status = this._getColorStatus(sx, y - 1, fillColor, targetColor);
						if (status > 0) {
							// the point match to the target color
							if (!northOpened) {
								// remember, that we already opened line for scan
								northOpened = status === 1;
								openList.push({x: sx, y: y - 1, status});
							} else if (status === 2) {
								northOpened = false;
							}
						} else {
							northOpened = false;
						}
					}
					// check south
					if (y < this._height) {
						status = this._getColorStatus(sx, y + 1, fillColor, targetColor);
						if (status > 0) {
							// the point match to the target color
							if (!southOpened) {
								// remember, that we already opened line for scan
								southOpened = status === 1;
								openList.push({x: sx, y: y + 1, status});
							} else if (status === 2) {
								southOpened = false;
							}
						} else {
							southOpened = false;
						}
					}
				}
			}
		}

		this._fillCanvas.ctx.putImageData(fillData, 0, 0);

		return true;
	}

	/**
	 * Checks picked color and returns status:
	 *  0 - not match
	 *  1 - exactly match, continue scanning
	 *  2 - partially match, stop scanning
	 *
	 * @param {number} x
	 * @param {number} y
	 * @param {Color} fillColor
	 * @param {Color} targetColor
	 * @returns {number}
	 * @private
	 */
	_getColorStatus(x, y, fillColor, targetColor) {
		if (this._getColor(x, y, fillColor).rgba === targetColor.rgba) {
			// exactly match
			return 1;
		}
		// check neighbor pixels
		if (this._getColor(x - 1, y, fillColor).rgba === fillColor.rgba
			|| this._getColor(x + 1, y, fillColor).rgba === fillColor.rgba
			|| this._getColor(x, y - 1, fillColor).rgba === fillColor.rgba
			|| this._getColor(x, y + 1, fillColor).rgba === fillColor.rgba) {
			// partially match
			return 2;
		}
		return 0;
	}

	/**
	 * Sets fill color for the specified pixel
	 *
	 * @param {number} x
	 * @param {number} y
	 * @param {Color} fillColor
	 * @param {number} alphaCoefficient
	 * @returns {boolean}
	 * @private
	 */
	_fillPoint(x, y, fillColor, alphaCoefficient = 1) {
		if (x <= 0 || x >= this._width) {
			// left or right border found
			// stop filling
			return false;
		}
		if (y <= 0 || y >= this._height) {
			// top or bottom border found
			// stop filling
			return false;
		}

		let fillPixels = this._fillPixels,
			values = fillColor.values;

		let i0 = y * this._width + x,
			i = i0 << 2,
			r1 = values[0],
			g1 = values[1],
			b1 = values[2],
			a1 = values[3],
			a0 = fillPixels[i + 3];

		this._filledPixels[i0] = 1;

		if (alphaCoefficient !== 1) {
			if (a0 > 200) {
				a0 *= alphaCoefficient;
			}
		}
		if (a0 < 255 || a1 < 255) {
			// colors alpha compositing
			// see https://stackoverflow.com/questions/28900598/how-to-combine-two-colors-with-varying-alpha-values
			a0 /= 255;
			a1 /= 255;
			let r0 = fillPixels[i],
				g0 = fillPixels[i + 1],
				b0 = fillPixels[i + 2];

			if (a0 < 1 && a1 === 1) {
				// fill color is opaque, background color is not
				// add background to the fill color
				let a0i = (1 - a0),
					a01 = a0i * a1 + a0;
				fillPixels[i] = Math.min((((r0 * a0) + (r1 * a1) * a0i) / a01) | 0, 255);
				fillPixels[i + 1] = Math.min((((g0 * a0) + (g1 * a1) * a0i) / a01) | 0, 255);
				fillPixels[i + 2] = Math.min((((b0 * a0) + (b1 * a1) * a0i) / a01) | 0, 255);
				fillPixels[i + 3] = Math.min((a01 * 255) | 0, 255);
			} else {
				// add fill color to background
				let a1i = (1 - a1),
					a11 = a1i * a0 + a1;
				fillPixels[i] = Math.min((((r1 * a1) + (r0 * a0) * a1i) / a11) | 0, 255);
				fillPixels[i + 1] = Math.min((((g1 * a1) + (g0 * a0) * a1i) / a11) | 0, 255);
				fillPixels[i + 2] = Math.min((((b1 * a1) + (b0 * a0) * a1i) / a11) | 0, 255);
				fillPixels[i + 3] = Math.min((a11 * 255) | 0, 255);
			}
		} else {
			fillPixels[i] = r1;
			fillPixels[i + 1] = g1;
			fillPixels[i + 2] = b1;
			fillPixels[i + 3] = 255;
		}
		return true;
	}

	/**
	 * Reads color of the pixel
	 *
	 * @param {number} x
	 * @param {number} y
	 * @param {Color} fillColor
	 * @returns {Color}
	 * @private
	 */
	_getColor(x, y, fillColor) {
		let i0 = y * this._width + x;
		if (this._filledPixels[i0] === 1) {
			return fillColor;
		}
		let pixels = this._fillPixels;
		let i = i0 << 2,
			i3 = i + 3;
		if (pixels[i3] > 0) {
			return new Color(
				pixels[i],
				pixels[i + 1],
				pixels[i + 2],
				pixels[i3]
			);
		} else {
			return this._transparentColor;
		}
	}
}

export default CanvasFloodFill;
